/**
 * Supporting-file edit modal
 *
 * The metadata-edit half of the review page's Supporting Files tab, ported
 * from the edit-mode branch of the old standalone upload page (its
 * `getSupportingFile` / `onUpdate` path). Two deliberate omissions from
 * that page:
 *
 * - No file-content upload — this edits metadata only.
 * - No period field — the tab is scoped to a single month, and letting a
 *   user edit a file out of that month would make it vanish from the list
 *   the moment they saved.
 *
 * `file` doubles as the open/closed flag: null closes the modal. Form state
 * is re-derived from `file` on every change so reopening on a different row
 * never shows the previous row's values.
 */

import { useEffect, useState } from "react";
import { Button, DialogFooter, Loader, Modal } from "@/ui/atoms";
import { FormField } from "@/ui/molecules";
import type { TSupportingFile } from "@/store";

export type TSupportingFileEditModalProps = {
    /** The row being edited; null closes the modal. */
    file: TSupportingFile | null;
    onOpenChange: (open: boolean) => void;
    relatableFiles: { id: string; name: string }[];
    onSave: (
        fileId: string,
        input: {
            friendlyName: string;
            notes: string | null;
            financialFileId: string | null;
            clearFinancialFile: boolean;
            rowVersion: string | null;
        },
    ) => Promise<void>;
    onSaved: () => void;
};

type TFormState = {
    friendlyName: string;
    financialFileId: string;
    notes: string;
    hadFinancialFile: boolean;
    rowVersion: string | null;
};

function formStateFromFile(file: TSupportingFile): TFormState {
    return {
        friendlyName: file.friendlyName,
        financialFileId: file.financialFileId ?? "",
        notes: file.notes ?? "",
        hadFinancialFile: file.financialFileId !== null,
        rowVersion: file.rowVersion,
    };
}

export function SupportingFileEditModal(props: TSupportingFileEditModalProps): React.ReactElement {
    const { file, onOpenChange, relatableFiles, onSave, onSaved } = props;

    const [form, setForm] = useState<TFormState | null>(file ? formStateFromFile(file) : null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Re-derive form state whenever the row being edited changes, so
    // reopening on a different row (or closing) never carries over the
    // previous row's values.
    useEffect(() => {
        setForm(file ? formStateFromFile(file) : null);
        setIsSubmitting(false);
        setError(null);
    }, [file]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file || !form || isSubmitting) return;

        setIsSubmitting(true);
        setError(null);
        try {
            await onSave(file.id, {
                friendlyName: form.friendlyName.trim(),
                notes: form.notes.trim() || null,
                financialFileId: form.financialFileId || null,
                clearFinancialFile: form.hadFinancialFile && !form.financialFileId,
                rowVersion: form.rowVersion,
            });
            onSaved();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Failed to save changes";
            setError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={file !== null}
            onOpenChange={onOpenChange}
            title="Edit Supporting File"
            className="max-h-[85vh] overflow-y-auto"
        >
            {file && form && (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <FormField label="Name" htmlFor="supporting-edit-name" required>
                        <input
                            id="supporting-edit-name"
                            data-testid="supporting-edit-name"
                            type="text"
                            value={form.friendlyName}
                            onChange={(e) => setForm({ ...form, friendlyName: e.target.value })}
                            disabled={isSubmitting}
                            maxLength={255}
                            required
                            className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    </FormField>

                    <FormField
                        label="Related file"
                        htmlFor="supporting-edit-related"
                        description="Optionally link to a specific financial file"
                    >
                        <select
                            id="supporting-edit-related"
                            data-testid="supporting-edit-related"
                            value={form.financialFileId}
                            onChange={(e) => setForm({ ...form, financialFileId: e.target.value })}
                            disabled={isSubmitting}
                            className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            <option value="">None (optional)</option>
                            {relatableFiles.map((relatable) => (
                                <option key={relatable.id} value={relatable.id}>
                                    {relatable.name}
                                </option>
                            ))}
                        </select>
                    </FormField>

                    <FormField
                        label="Notes"
                        htmlFor="supporting-edit-notes"
                        description={`${form.notes.length}/500 characters`}
                    >
                        <textarea
                            id="supporting-edit-notes"
                            data-testid="supporting-edit-notes"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            disabled={isSubmitting}
                            maxLength={500}
                            rows={3}
                            className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                        />
                    </FormField>

                    {error && (
                        <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                            {error}
                        </div>
                    )}

                    <DialogFooter className="gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="supporting-edit-save"
                            disabled={!form.friendlyName.trim() || isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader size="sm" className="mr-2" />
                                    Saving...
                                </>
                            ) : (
                                "Save changes"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            )}
        </Modal>
    );
}
